<script setup lang="ts">
/**
 * Компонент для отображения и выбора исключаемых ингредиентов
 * Универсальный для desktop и mobile (DRY principle)
 * Следует Vue Style Guide: Priority A
 */

import { computed } from 'vue';
import type { ProductExclude } from '../model/types/product.types';

interface Props {
  excludes: ProductExclude[];
  modelValue: number[];
  showLimit?: number;
  isMobile?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: number[]): void;
  (e: 'show-more'): void;
}

const props = withDefaults(defineProps<Props>(), {
  showLimit: 8,
  isMobile: false,
});

const emit = defineEmits<Emits>();

// Локальное значение для v-model
const selectedExcludes = computed({
  get: () => props.modelValue,
  set: (value: number[]) => emit('update:modelValue', value),
});

// Переключить исключение
const toggleExclude = (excludeId: number) => {
  const index = selectedExcludes.value.indexOf(excludeId);
  const newValue = [...selectedExcludes.value];
  
  if (index > -1) {
    newValue.splice(index, 1);
  } else {
    newValue.push(excludeId);
  }
  
  selectedExcludes.value = newValue;
};

// Проверка выбрано ли исключение
const isSelected = (excludeId: number) => selectedExcludes.value.includes(excludeId);
</script>

<template>
  <div v-if="excludes.length" class="product-excludes">
    <div class="product-excludes__title">
      {{ isMobile ? 'Убрать ингредиенты' : 'Исключить ингредиенты' }}
    </div>

    <!-- Desktop: список с чекбоксами -->
    <ul v-if="!isMobile" class="product-excludes__list">
      <li
        v-for="exclude in excludes"
        :key="exclude.id"
        class="product-excludes__list-item"
      >
        <img
          v-if="exclude.img && exclude.img.url"
          :src="exclude.img.url"
          :alt="exclude.name"
          class="product-excludes__list-image"
        />
        <div class="product-excludes__list-text">
          {{ exclude.name }}
        </div>
        <UiKitCheckbox
          v-model="selectedExcludes"
          :value="exclude.id"
          color="success"
          size="lg"
          class="ml-auto"
        />
      </li>
    </ul>

    <!-- Mobile: теги с зачеркиванием -->
    <div v-else class="product-excludes__tags">
      <div
        v-for="exclude in excludes"
        :key="exclude.id"
        class="product-excludes__tag"
        :class="isSelected(exclude.id) ? 'product-excludes__tag--selected' : ''"
        @click="toggleExclude(exclude.id)"
      >
        <!-- Название с зачеркиванием для выбранных -->
        <div
          class="product-excludes__tag-name"
          :class="isSelected(exclude.id) ? 'line-through' : ''"
        >
          {{ exclude.name }}
        </div>

        <!-- Индикатор выбора (X иконка) -->
        <div v-if="isSelected(exclude.id)" class="product-excludes__tag-icon">
          <svg class="w-3 h-3 text-hui-success" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>
    </div>

    <!-- Кнопка "Показать ещё" -->
    <slot name="show-more-button" />
  </div>
</template>

<style scoped lang="scss">
.product-excludes {
  // Кегль и зазоры на 1024–1279 мельче — по той же причине, что и у добавок:
  // правая колонка модалки там ~430px (см. ProductAddons)
  &__title {
    @apply text-sm xl:text-base font-semibold my-2;

    @media (max-width: 768px) {
      @apply text-white;
    }
  }

  // Desktop список
  &__list {
    @apply grid grid-cols-1 gap-3 xl:gap-4;
  }

  &__list-item {
    @apply flex items-center gap-2 xl:gap-3 justify-between;
  }

  &__list-image {
    @apply w-7 h-7 xl:w-8 xl:h-8 object-contain rounded;
  }

  &__list-text {
    @apply font-normal text-xs xl:text-sm;

    @media (max-width: 768px) {
      @apply text-white;
    }
  }

  // Mobile теги
  &__tags {
    @apply flex flex-wrap gap-2;
  }

  &__tag {
    @apply relative px-3 py-2 rounded-full cursor-pointer transition-all duration-200 bg-hui-success/15;

    &--selected {
      @apply bg-hui-success;
    }
  }

  &__tag-name {
    @apply text-sm font-medium text-white text-center whitespace-nowrap;
  }

  &__tag-icon {
    @apply absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center;
  }
}
</style>

